import { request } from "../api/client";

const AUTH_SESSION_STORAGE_KEY = "puck-pickup-auth-session";
const EXPIRY_SAFETY_WINDOW_SECONDS = 30;

const nowSeconds = () => Math.floor(Date.now() / 1000);

export const storeAuthSession = (tokenSet) => {
  const expiresAt = nowSeconds() + Number(tokenSet.expires_in || 0);
  const payload = {
    accessToken: tokenSet.access_token,
    refreshToken: tokenSet.refresh_token || "",
    idToken: tokenSet.id_token || "",
    tokenType: tokenSet.token_type || "Bearer",
    scope: tokenSet.scope || "",
    provider: tokenSet.provider || "google",
    expiresAt,
  };
  localStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(payload));
  // eslint-disable-next-line no-console
  console.log("[auth] auth session stored in localStorage", {
    key: AUTH_SESSION_STORAGE_KEY,
    hasAccessToken: Boolean(payload.accessToken),
    expiresAt: payload.expiresAt,
  });
  return payload;
};

export const getAuthSession = () => {
  const raw = localStorage.getItem(AUTH_SESSION_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch (_error) {
    localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
    return null;
  }
};

export const clearAuthSession = () => {
  localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
  // eslint-disable-next-line no-console
  console.log("[auth] cleared auth session");
};

export const getAccessToken = () => getAuthSession()?.accessToken || "";

export const hasLocallyValidSession = () => {
  const session = getAuthSession();
  if (!session?.accessToken || !session?.expiresAt) {
    return false;
  }

  return session.expiresAt - EXPIRY_SAFETY_WINDOW_SECONDS > nowSeconds();
};

export const introspectCurrentSession = async () => {
  const token = getAccessToken();
  if (!token) {
    return { active: false };
  }

  try {
    const result = await request("/auth/google/me", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { active: true, ...result };
  } catch (_error) {
    clearAuthSession();
    return { active: false };
  }
};

const POST_LOGIN_REDIRECT_KEY = "puck-pickup-post-login-redirect";

export const savePostLoginRedirect = (path) => {
  sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
};

export const consumePostLoginRedirect = () => {
  const path = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  return path || null;
};

export const isUserSignedIn = async ({ verifyWithIntrospection = false } = {}) => {
  if (!hasLocallyValidSession()) {
    return false;
  }

  if (!verifyWithIntrospection) {
    return true;
  }

  const introspection = await introspectCurrentSession();
  return Boolean(introspection.active);
};

export const fetchCurrentUserProfile = async () => {
  const token = getAccessToken();
  if (!token || !hasLocallyValidSession()) {
    return null;
  }

  try {
    const me = await request("/auth/google/me", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return me;
  } catch (_error) {
    clearAuthSession();
    return null;
  }
};
